// Patient-data scrubbing for the website's error reports (Sentry SDK → GlitchTip).
//
// scrubText and maskPhone are ports of the backend's logmask.Scrub and
// logmask.Phone (backend/internal/logmask/logmask.go). scrub.test.ts runs
// backend/internal/logmask/testdata/scrub_shared_cases.json against scrubText,
// the same table the backend and messaging/redact run, so change them together.
//
// scrubForReport is what the Sentry hooks use. A browser error report can hold
// whatever the page had on screen, so it goes further than the backend's rules:
//
//   - the query string (and, for absolute URLs, the fragment) of a URL inside
//     the text is removed: search=, phone= and iin= carry patient identifiers;
//   - an email becomes "***@***";
//   - after scrubText, any run of 10 or more digits still left becomes "***"
//     plus its last four digits wherever it stands: a WhatsApp LID
//     ("117033714868226@lid"), an agent JID ("77011234567_1@lid"), a number
//     glued to other text. That also masks unix-millisecond timestamps and long
//     segments of study UIDs, which is an accepted loss in an error report.
//
// Neither can find a patient's name. Keep free-form values (console arguments,
// request bodies) out of reports altogether; sentry-scrub.ts drops them.

// kzPhone is a Kazakh number: +7, 8 or a bare 7 (as in JIDs), then ten digits
// starting with 7, with an optional space or dash between the groups and
// optional brackets around the code. Same pattern as the backend's.
const KZ_PHONE = /(?:\+7|8|7)[ -]?\(?7\d{2}\)?[ -]?\d{3}[ -]?\d{2}[ -]?\d{2}/g

// A run of 12 digits: an IIN, or anything else that long.
const TWELVE_DIGITS = /\d{12}/g

// scrubText works on at most this many characters; a cut input ends in "…".
// The backend counts bytes, this counts UTF-16 code units.
export const MAX_SCRUB_CHARS = 4096

/**
 * maskPhone masks a phone number or WhatsApp id to "***" plus the last four
 * digits of its user part: the text before "@", then before any ":" (device)
 * or "_" (agent) suffix. Empty input gives "", fewer than four digits "***".
 */
export function maskPhone(s: string): string {
    if (s === "") return ""
    let user = s
    const at = user.indexOf("@")
    if (at >= 0) user = user.slice(0, at)
    const suffix = user.search(/[:_]/)
    if (suffix >= 0) user = user.slice(0, suffix)
    const d = user.replace(/\D/g, "")
    return d.length < 4 ? "***" : "***" + d.slice(-4)
}

/**
 * scrubText masks Kazakh phone numbers (to "***" plus the last four digits)
 * and standalone 12-digit numbers (to "***") inside free text. A match only
 * counts when it stands alone, so digits inside study UIDs, accession numbers,
 * UUIDs, file names, decimals, dates and times are left as they are. Exact
 * parity with the backend's logmask.Scrub.
 */
export function scrubText(input: string): string {
    let s = input
    const cut = s.length > MAX_SCRUB_CHARS
    if (cut) s = capText(s, MAX_SCRUB_CHARS)
    s = replaceStandalone(s, KZ_PHONE, maskPhone)
    s = replaceStandalone(s, TWELVE_DIGITS, () => "***")
    return cut ? s + "…" : s
}

// An absolute URL's query string and fragment.
const ABSOLUTE_URL_QUERY = /\b(https?:\/\/[^\s?#"'<>`]*)[?#][^\s"'<>`]*/gi
// A path's query string, when the path starts the text or follows a delimiter.
const PATH_QUERY = /(^|[\s"'(=,:[])(\/[^\s?#"'<>`]*)\?[^\s"'<>`]*/g
// An email address. WhatsApp's own domains are JIDs, not emails; scrubText
// masks their user part instead. The bounded quantifiers keep the scan linear.
const EMAIL =
    /[A-Za-z0-9._%+-]{1,64}@(?!(?:s\.whatsapp\.net|c\.us|g\.us)(?![A-Za-z0-9.-]))[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63}){0,8}\.[A-Za-z]{2,24}/g
const LONG_DIGIT_RUN = /\d{10,}/g

/** removeUrlQueries removes query strings from the URLs and paths inside text. */
export function removeUrlQueries(s: string): string {
    if (!s.includes("?") && !s.includes("#")) return s
    return s.replace(ABSOLUTE_URL_QUERY, "$1").replace(PATH_QUERY, "$1$2")
}

/**
 * scrubForReport is scrubText plus the stricter rules for error reports: URL
 * query strings removed, emails masked, and any run of 10+ digits masked.
 * Input longer than MAX_SCRUB_CHARS is cut first, before any pattern runs, and
 * the cut drops the whole token it lands in (half an email or phone is not
 * recognisable). Running it twice on short text gives the same result.
 */
export function scrubForReport(input: string): string {
    const cut = input.length > MAX_SCRUB_CHARS
    let s = cut ? capAtToken(input, MAX_SCRUB_CHARS) : input
    s = removeUrlQueries(s)
    if (s.includes("@")) s = s.replace(EMAIL, "***@***")
    s = scrubText(s)
    s = s.replace(LONG_DIGIT_RUN, (run) => "***" + run.slice(-4))
    return cut ? s + "…" : s
}

// capAtToken cuts s to at most limit characters, then drops the partial token
// (the run of non-whitespace) the cut landed in.
function capAtToken(s: string, limit: number): string {
    if (/\s/.test(s.charAt(limit))) return s.slice(0, limit)
    let n = limit
    while (n > 0 && !/\s/.test(s.charAt(n - 1))) n--
    return s.slice(0, n)
}

/** stripQuery cuts a URL or path at its query string or fragment. */
export function stripQuery(url: string): string {
    const i = url.search(/[?#]/)
    return i >= 0 ? url.slice(0, i) : url
}

// capText returns at most n characters of s without splitting a surrogate
// pair. A digit run the cut would split is dropped whole, so half a phone
// number or IIN (too short to be recognised and masked) never survives.
function capText(s: string, limit: number): string {
    let n = limit
    const low = s.charCodeAt(n)
    if (n > 0 && low >= 0xdc00 && low <= 0xdfff) n--
    if (isDigit(s.charCodeAt(n))) {
        while (n > 0 && isDigit(s.charCodeAt(n - 1))) n--
    }
    return s.slice(0, n)
}

// replaceStandalone replaces each match of re that stands alone. A match that
// is part of a larger token is skipped and the search resumes one character
// later, as the backend does.
function replaceStandalone(s: string, re: RegExp, repl: (match: string) => string): string {
    let out = ""
    let pos = 0
    while (pos < s.length) {
        re.lastIndex = pos
        const m = re.exec(s)
        if (!m) break
        const start = m.index
        const end = start + m[0].length
        if (!standalone(s, start, end)) {
            out += s.slice(pos, start + 1)
            pos = start + 1
            continue
        }
        out += s.slice(pos, start) + repl(m[0])
        pos = end
    }
    return out + s.slice(pos)
}

// standalone reports whether s[start:end] is a token of its own: the character
// on either side is not a letter, digit or underscore, and a dot or dash there
// does not join the match to another letter or digit.
function standalone(s: string, start: number, end: number): boolean {
    if (start > 0) {
        const l = s.charCodeAt(start - 1)
        if (isWordChar(l)) return false
        if ((l === DOT || l === DASH) && start > 1 && isWordChar(s.charCodeAt(start - 2))) return false
    }
    if (end < s.length) {
        const r = s.charCodeAt(end)
        if (isWordChar(r)) return false
        if ((r === DOT || r === DASH) && end + 1 < s.length && isWordChar(s.charCodeAt(end + 1))) return false
    }
    return true
}

const DOT = 0x2e
const DASH = 0x2d

// isWordChar is a letter, digit or underscore. Every non-ASCII character counts
// as a letter, so digits glued to Cyrillic text stay intact, as on the backend.
function isWordChar(c: number): boolean {
    return (
        isDigit(c) ||
        c === 0x5f ||
        (c >= 0x61 && c <= 0x7a) ||
        (c >= 0x41 && c <= 0x5a) ||
        c >= 0x80
    )
}

function isDigit(c: number): boolean {
    return c >= 0x30 && c <= 0x39
}
